import { useCallback, useEffect, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Loader2, MessageSquare, RotateCcw, Send, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { TranscriptDisplay } from '@/entities/call';

type ChatState = 'idle' | 'connecting' | 'open' | 'ended'

interface ChatbotButtonProps {
  campaignId: number
  agentId: string
  disabled?: boolean
}

export function ChatbotButton({ campaignId, agentId, disabled }: ChatbotButtonProps) {
  const { user } = useUser()
  const wsRef = useRef<WebSocket | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [chatState, setChatState] = useState<ChatState>('idle')
  const [messages, setMessages] = useState<any[]>([])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Cleanup on unmount
  useEffect(() => {
    return () => { wsRef.current?.close() }
  }, [])

  const openChat = useCallback(() => {
    if (!user) return
    const wsBase = import.meta.env.VITE_CHATBOT_WS_URL || 'ws://localhost:3007'
    const url = `${wsBase}?campaign_id=${campaignId}&customer_id=${user.id}&agent_id=${agentId}`

    setChatState('connecting')
    setMessages([])

    const ws = new WebSocket(url)
    wsRef.current = ws

    // Send init on open — works in AWS where $connect can't send messages back.
    // In local dev, this may be lost (race with async setup), but 'ready' covers it.
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'init' }))
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'ready') {
          // Local dev: server signals setup complete — send init (the onopen one was lost)
          ws.send(JSON.stringify({ type: 'init' }))
        } else if (data.type === 'connected') {
          setChatState('open')
          setMessages(data.messages || [])
        } else if (data.type === 'typing') {
          setIsTyping(true)
        } else if (data.type === 'messages') {
          setIsTyping(false)
          // Strip optimistic (negative-id) entries before appending real server records
          setMessages((prev) => [...prev.filter((m) => (m.id ?? 0) >= 0), ...(data.messages || [])])
        } else if (data.type === 'ended') {
          setIsTyping(false)
          setChatState('ended')
        } else if (data.type === 'error') {
          console.error('[chat] Server error:', data.message)
        }
      } catch (e) {
        console.error('[chat] Failed to parse message:', e)
      }
    }

    ws.onerror = () => {
      setChatState('idle')
      wsRef.current = null
    }

    ws.onclose = () => {
      wsRef.current = null
      setChatState((prev) => (prev !== 'ended' ? 'idle' : 'ended'))
    }
  }, [user, campaignId, agentId])

  const sendMessage = useCallback(() => {
    const text = inputText.trim()
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify({ type: 'message', text }))
    setInputText('')
    // Optimistic update — show the user message immediately before server echo
    setMessages((prev) => [...prev, { id: -Date.now(), role: 'user', content: text }])
  }, [inputText])

  const restartChat = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify({ type: 'restart' }))
    setChatState('connecting')
    setMessages([])
    setIsTyping(false)
  }, [])

  const closeChat = useCallback(() => {
    wsRef.current?.close()
    wsRef.current = null
    setChatState('idle')
    setMessages([])
    setInputText('')
  }, [])

  // Map chat_messages records to TranscriptDisplay events (turn / tool_call / tool_result)
  const transcriptEvents = messages.flatMap((m) => {
    if (m.role === 'user') {
      return [{ type: 'turn' as const, role: 'Callee', text: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }]
    }
    if (m.role === 'assistant') {
      const events: unknown[] = []
      // Emit a tool_call event for each tool invocation (collapsible in TranscriptDisplay)
      if (m.tool_calls) {
        const calls = Array.isArray(m.tool_calls) ? m.tool_calls : []
        for (const tc of calls) {
          const name = tc?.function?.name ?? tc?.name ?? 'tool'
          let args: unknown = tc?.function?.arguments
          try { args = typeof args === 'string' ? JSON.parse(args) : args } catch { /* leave as string */ }
          events.push({ type: 'tool_call', name, args, id: tc?.id })
        }
      }
      if (m.content) {
        events.push({ type: 'turn', role: 'Agent', text: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) })
      }
      return events
    }
    if (m.role === 'tool') {
      let result: unknown = m.content
      try { result = typeof m.content === 'string' ? JSON.parse(m.content) : m.content } catch { /* leave as string */ }
      return [{ type: 'tool_result' as const, name: m.name ?? 'tool', status: 'success' as const, result, id: m.tool_call_id }]
    }
    return []
  })

  if (chatState === 'idle') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={openChat}
        disabled={disabled || !user}
        className="h-6 w-6 p-0"
        title="Chat with bot"
      >
        <MessageSquare className="w-2.5 h-2.5" />
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled
        className={`h-6 w-6 p-0 ${chatState === 'ended' ? 'bg-gray-100 border-gray-300' : 'bg-blue-100 border-blue-300'}`}
        title={chatState === 'ended' ? 'Chat ended' : 'Chat open'}
      >
        {chatState === 'connecting' ? (
          <Loader2 className="w-2.5 h-2.5 animate-spin" />
        ) : (
          <MessageSquare className={`w-2.5 h-2.5 ${chatState === 'ended' ? 'text-gray-500' : 'text-blue-600'}`} />
        )}
      </Button>

      {/* Floating Chat Panel */}
      <div className="fixed bottom-4 right-4 z-50 w-[500px] max-h-[90vh] flex flex-col">
        <Card className={`shadow-lg ${chatState === 'ended' ? 'border-gray-200 bg-gray-50' : 'border-blue-200'}`}>
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                {chatState === 'connecting' && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                {chatState !== 'connecting' && (
                  <MessageSquare className={`w-4 h-4 ${chatState === 'ended' ? 'text-gray-500' : 'text-blue-600'}`} />
                )}
                {chatState === 'connecting' ? 'Connecting...' : chatState === 'ended' ? 'Chat Ended' : 'Chat'}
              </CardTitle>
              <div className="flex items-center gap-1">
                {chatState === 'open' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={restartChat}
                    className="h-6 w-6 p-0 hover:bg-gray-200"
                    title="Restart conversation"
                  >
                    <RotateCcw className="w-3.5 h-3.5 text-gray-500" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeChat}
                  className="h-6 w-6 p-0 hover:bg-gray-200"
                  title="Close"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="px-4 pb-4 pt-0">
            {/* Messages */}
            <div className="flex-1 min-h-0 max-h-[calc(90vh-120px)] overflow-y-auto bg-white rounded border border-gray-100 p-1 mb-2">
              {transcriptEvents.length === 0 && chatState === 'open' && (
                <p className="text-xs text-gray-400 text-center py-1">Start a conversation...</p>
              )}
              {transcriptEvents.length > 0 && (
                <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
              )}
              {isTyping && (
                <div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Typing...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Restart prompt when session ended */}
            {chatState === 'ended' && (
              <Button
                variant="outline"
                size="sm"
                onClick={restartChat}
                className="w-full h-7 text-xs text-gray-500 border-gray-300 hover:bg-gray-100"
              >
                <RotateCcw className="w-3 h-3 mr-1.5" />
                Restart Session
              </Button>
            )}

            {/* Input */}
            {chatState === 'open' && (
              <div className="flex gap-2">
                <Input
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="h-7 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={sendMessage}
                  disabled={!inputText.trim()}
                  className="h-7 px-2"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
